import type { GpbDefinition } from '../../types/gpb';

/**
 * ⭐⭐ **ATHLETE — RANK 1, TRANSLATED BY HAND FROM THE AUTHORED PACKAGE.**
 *
 * Source: `GPB Development/Athlete GPB - Rank 1 Content Package v0.1.md` and the
 * content manifest, which is an authoring register and explicitly *"NOT a native
 * import or executable rule schema"*. Every condition below was a sentence; the
 * sentence is quoted beside its translation so the two can be checked against
 * each other by eye.
 *
 * ⚠️⚠️ **ONE BUILD IS TRANSLATED BY HAND. THE OTHER SIX ARE NOT.** This exists to
 * prove the schema expresses real authored content — builds 2–7 are authored
 * directly in the executable form. If a second build ever arrives as prose, that
 * is a process failure, not a translation job.
 *
 * ⏳ **RANKS 1–2 ONLY.** Ranks 3–7 are not translated. What they still add:
 * ordered occurrence sets (Q12-G01, "completed in their logical order"),
 * `after` chains (Q15-G06, two reviews "with an actual subsequent practice
 * occurrence between them"), `mode: 'any'` (Q10-G02, one supported alternative)
 * and the remaining saved records. Ranks 1–2 exercise everything else: all five
 * predicate shapes, both counting channels, and the only three-quest rank.
 */
/**
 * AP01's seventeen core occurrences, in authored order — "A00, S01–S12 and
 * R01–R04". ⚠️ The R-occurrences are INTERLEAVED, not appended: R01 sits after
 * S03. Written out rather than generated, because the order is authored content
 * and a generator would invent a tidier one.
 */
const AP01_CORE = [
  'AP01-A00',
  'AP01-S01', 'AP01-S02', 'AP01-S03', 'AP01-R01',
  'AP01-S04', 'AP01-S05', 'AP01-S06', 'AP01-R02',
  'AP01-S07', 'AP01-S08', 'AP01-S09', 'AP01-R03',
  'AP01-S10', 'AP01-S11', 'AP01-S12', 'AP01-R04',
] as const;

/**
 * AP03's daily eating/hydration occurrences. Each authored day carries TWO — an
 * `-E` and an `-H` — so "D01–D14" is 28 occurrences, not 14. Rank 2's milestone
 * is N01–N06 plus those: 34.
 */
const ap03Days = (through: number, suffix: 'E' | 'H'): string[] =>
  Array.from({ length: through }, (_, i) => `AP03-D${String(i + 1).padStart(2, '0')}-${suffix}`);

/** AP02's twenty-eight: Blocks A (10), B (9) and C (9), in authored order. */
const AP02_CORE = [
  'AP02-A-L01', 'AP02-A-L02', 'AP02-A-L03', 'AP02-A-S01', 'AP02-A-S02',
  'AP02-A-S03', 'AP02-A-S04', 'AP02-A-S05', 'AP02-A-S06', 'AP02-A-R01',
  'AP02-B-L01', 'AP02-B-L02', 'AP02-B-S01', 'AP02-B-S02', 'AP02-B-S03',
  'AP02-B-S04', 'AP02-B-S05', 'AP02-B-S06', 'AP02-B-R01',
  'AP02-C-L01', 'AP02-C-L02', 'AP02-C-S01', 'AP02-C-S02', 'AP02-C-S03',
  'AP02-C-S04', 'AP02-C-S05', 'AP02-C-S06', 'AP02-C-R01',
];

/** AP04's twenty-six: three openings, fourteen sleep dates, seven recovery, two reviews. */
const AP04_CORE = [
  'AP04-O01', 'AP04-O02', 'AP04-O03',
  ...Array.from({ length: 14 }, (_, i) => `AP04-D${String(i + 1).padStart(2, '0')}-S`),
  ...Array.from({ length: 7 }, (_, i) => `AP04-P0${i + 1}`),
  'AP04-R01', 'AP04-R02',
];

export const ATHLETE: GpbDefinition = {
  id: 'athlete-home',
  version: '0.1',
  /* ⚠️ FIRST RELEASE, AND `supersedes` IS ABSENT BECAUSE THERE IS NOTHING TO
     DIFF. Leaving it out is the honest state: a first release that named a
     predecessor would make `releaseDiff` invent a comparison against a version
     that never shipped. The second Athlete release sets `supersedes: '0.1'`, and
     the change list is derived from the two definitions rather than written
     here — see `GpbRelease` for why an authored list is not allowed to exist. */
  release: {
    version: '0.1',
    publishedOn: '2026-09-20',
    summary: 'First release. Seven ranks, fifteen quests and fifty-eight requirements, '
      + 'translated from the Athlete build package.',
  },
  /**
   * ⭐⭐ **THE OWNER'S OWN ARCHITECTURE (D42), AUTHORED 2026-09-22.** Nothing here
   * was invented — the numbers, the domain assignments and the premise are the
   * owner's. An earlier 3 Fitness / 3 Nourishment / 3 Rest split WAS invented by
   * Claude, shown in the prototype, and rejected; hole H2 existed because the app
   * then correctly refused to show anything at all.
   *
   * ⚠️⚠️ **THIS IS THE FULL COMMITMENT ONLY.** D4 says commitment varies the
   * loadout, so `steady` and `light` want their own. They are NOT authored, and
   * nothing derives them by scaling these numbers — a proportional cut is
   * arithmetic nobody chose. The surface says they are absent.
   *
   * ⚠️ Ranges are load-bearing: Occupational is 0.5–1 h because it genuinely
   * differs for a professional and an amateur whose real occupation is elsewhere.
   * The range says so without asking, which matters — the package forbids an
   * entry test.
   *
   * ⚠️ Material is stored as `ecological`.
   */
  loadout: {
    dominant: 'personal',
    premise: 'The Athlete organizes their day around training, adaptation and readiness — '
      + 'using the other life domains to support sustainable physical performance.',
    sleep: { low: 8, high: 9 },
    domains: [
      { domain: 'personal', low: 6, high: 8, role: 'develops the body',
        carries: ['Sport practice', 'Strength and conditioning', 'Mobility', 'Recovery',
          'Rehabilitation and prehab', 'Nutrition', 'Hydration', 'Sleep', 'Body care'] },
      { domain: 'social', low: 1, high: 2, role: 'provides the support',
        carries: ['Coach relationship', 'Teammates', 'Training partners', 'Family',
          'Friends', 'Support team'] },
      { domain: 'intellectual', low: 0.75, high: 1.5, role: 'understands performance',
        carries: ['Tactical learning', 'Video analysis', 'Sport science',
          'Nutrition education', 'Opponent analysis', 'Performance-data review'] },
      { domain: 'ecological', low: 0.75, high: 1.5, role: 'prepares the environment',
        carries: ['Equipment maintenance and selection', 'Training clothing', 'Meal preparation',
          'Training bag', 'Travel logistics', 'Wearables and data devices'] },
      /* ⚠️ The conditional one. Large for a professional; small for an amateur
         whose actual occupation exists separately. */
      { domain: 'occupational', low: 0.5, high: 1, role: 'manages the career',
        carries: ['Sponsor obligations', 'Media', 'Federation and team administration',
          'Competition registration', 'Career management'] },
      { domain: 'avocational', low: 0.5, high: 1, role: 'provides psychological recovery',
        carries: ['Entertainment', 'Games', 'Reading', 'Music', 'Low-demand recreation'] },
      /* ⚠️ The owner drew this line explicitly: TACTICAL cognition is Intellectual;
         meaning, identity and inner regulation are Existential. Not all sport
         psychology lands in one place. */
      { domain: 'existential', low: 0.25, high: 0.5, role: 'develops the inner competitor',
        carries: ['Mental preparation', 'Visualization', 'Reflection', 'Values and identity',
          'Handling wins and losses'] },
    ],
  },

  /**
   * ⚠️⚠️ **AN EXAMPLE, NOT A STANDARD (D41).** A full-time competitive athlete's
   * two-session day, while the package's authored audience is a beginner or
   * returning adult. Under D41 that is deliberate: the time layer is a suggestion,
   * so this is *the shape of an athlete's life* rather than something anybody is
   * failing to meet. ▶ Whether a second, scaled template is also wanted is §6 Q5.
   */
  dayTemplate: {
    title: 'A two-session training day',
    caveat: 'Not every athlete follows these times. Training load is sport-, phase-, age- '
      + 'and individual-specific, and the IOC\u2019s own guidance emphasises individualised '
      + 'load management over maximising training hours.',
    blocks: [
      { from: '06:30', to: '07:00', label: 'Wake, hydration, readiness check, light mobility', domain: 'personal' },
      { from: '07:00', to: '07:45', label: 'Breakfast and fuelling', domain: 'personal' },
      { from: '07:45', to: '08:15', label: 'Pack equipment and travel to training', domain: 'ecological' },
      { from: '08:15', to: '10:15', label: 'Primary sport session', domain: 'personal' },
      { from: '10:15', to: '11:00', label: 'Cooldown, mobility, rehab and recovery', domain: 'personal' },
      { from: '11:00', to: '11:30', label: 'Recovery nutrition and shower', domain: 'personal' },
      { from: '11:30', to: '12:30', label: 'Film review, tactics, sport learning', domain: 'intellectual' },
      { from: '12:30', to: '13:15', label: 'Lunch', domain: 'personal' },
      { from: '13:15', to: '14:00', label: 'Nap or passive recovery', domain: 'personal' },
      { from: '14:00', to: '14:30', label: 'Equipment and preparation', domain: 'ecological' },
      { from: '14:30', to: '16:00', label: 'Strength and conditioning, or second session', domain: 'personal' },
      { from: '16:00', to: '16:45', label: 'Recovery and snack', domain: 'personal' },
      { from: '16:45', to: '17:30', label: 'Athlete admin, coach planning, sponsors', domain: 'occupational' },
      { from: '17:30', to: '18:30', label: 'Family, friends, teammates', domain: 'social' },
      { from: '18:30', to: '19:15', label: 'Dinner', domain: 'personal' },
      { from: '19:15', to: '20:15', label: 'Hobby, entertainment, decompression', domain: 'avocational' },
      { from: '20:15', to: '20:45', label: 'Visualization, reflection, breathing', domain: 'existential' },
      { from: '20:45', to: '21:30', label: 'Prepare clothes, equipment and meals for tomorrow', domain: 'ecological' },
      { from: '21:30', to: '22:00', label: 'Wind-down routine', domain: 'personal' },
      { from: '22:00', to: '06:30', label: 'Sleep — 8.5 hours', domain: 'personal' },
    ],
  },

  author: 'Metanoia',
  name: 'Athlete',
  promise: 'Develop the habits, understanding and practical rhythm that let an athletic lifestyle become part of your everyday life.',

  /* ⚠️ Names from the manifest's own `habits` and `ability.skills`, so the thing
     the app creates is called what the package calls it. Domains and areas from
     each rank's "content-to-area mapping" — AP01 and H01 are Personal → Fitness;
     AP03, H02 and H03 are Personal → Nourishment; the ability is Personal. */
  components: {
  /* ⚠️⚠️ ONE MODULE PER OCCURRENCE, even where several reuse one card — the
     author's rule (AP01: "Give every occurrence its own module identity"). AP01
     runs SEVENTEEN occurrences on THIRTEEN cards: `Strength Week 1` is performed
     at S01 and again at S03, and each is its own module. That is what makes the
     occurrence→module binding one-to-one by construction.
     ⚠️ Days describe the planned run and are never a deadline — "the user may move
     an unfinished occurrence while keeping the intended spacing". */
  programs: [
    {
      id: 'AP01', title: 'Foundation Prep', domain: 'personal',
      occurrences: [
        { id: 'AP01-A00', day: 1, card: 'Opening guide' },
        { id: 'AP01-S01', day: 1, card: 'Strength Week 1' },
        { id: 'AP01-S02', day: 3, card: 'Walking Week 1' },
        { id: 'AP01-S03', day: 5, card: 'Strength Week 1' },
        { id: 'AP01-R01', day: 7, card: 'Prepare next week\'s opportunities' },
        { id: 'AP01-S04', day: 8, card: 'Strength Week 2' },
        { id: 'AP01-S05', day: 10, card: 'Walking Week 2' },
        { id: 'AP01-S06', day: 12, card: 'Strength Week 2' },
        { id: 'AP01-R02', day: 14, card: 'Retain or adjust the next week\'s starting amount' },
        { id: 'AP01-S07', day: 15, card: 'Strength Week 3' },
        { id: 'AP01-S08', day: 17, card: 'Walking Week 3' },
        { id: 'AP01-S09', day: 19, card: 'Strength Week 3' },
        { id: 'AP01-R03', day: 21, card: 'Prepare the final week' },
        { id: 'AP01-S10', day: 22, card: 'Strength Week 4' },
        { id: 'AP01-S11', day: 24, card: 'Walking Week 4' },
        { id: 'AP01-S12', day: 26, card: 'Strength Week 4' },
        { id: 'AP01-R04', day: 28, card: 'Save the version to continue' },
      ],
    },
    {
      id: 'AP03', title: 'Eating Practice', domain: 'personal',
      /* ⚠️⚠️ SIXTY-FIVE OCCURRENCES, AND ONLY NINE ARE IN A TABLE. The other 56 are
         authored as a RULE, quoted here so nobody re-derives it: "Author daily
         eating occurrences as AP03-D01-E through AP03-D28-E, and daily hydration
         occurrences as AP03-D01-H through AP03-D28-H. Each is a separate
         occurrence… These IDs define all 56 daily scheduled application
         occurrences; N01–N09 define nine additional core modules."
         ⚠️ Generated from that sentence rather than pasted 56 times — a
         transcription of 56 near-identical ids is a typo waiting to happen, and
         `athlete.test.ts` pins the count and the endpoints. */
      occurrences: [
        { id: 'AP03-N01', day: 1, card: 'Read the starter concepts' },
        { id: 'AP03-N02', day: 1, card: 'Prepare the first meal and water arrangement' },
        { id: 'AP03-N03', day: 4, card: 'Prepare the next two selected meals' },
        { id: 'AP03-N04', day: 8, card: 'Review the first week' },
        { id: 'AP03-N05', day: 11, card: 'Adjust the arrangement' },
        { id: 'AP03-N06', day: 15, card: 'Prepare the third week' },
        { id: 'AP03-N07', day: 18, card: 'Review what is working' },
        { id: 'AP03-N08', day: 22, card: 'Prepare the final week' },
        { id: 'AP03-N09', day: 28, card: 'Save the version to continue' },
        ...Array.from({ length: 28 }, (_, i) => {
          const day = i + 1;
          const n = String(day).padStart(2, '0');
          return [
            { id: `AP03-D${n}-E`, day, card: 'Daily eating application' },
            { id: `AP03-D${n}-H`, day, card: 'Daily hydration application' },
          ];
        }).flat(),
      ],
    },
  ],
    habits: [
      /* ⚠️ `behaviour` is each habit's own "Behavior:"/"Counts when" line from the
         package — what actually qualifies — because the app requires a
         description and inventing one would put words in the author's mouth. */
      { id: 'H01', title: 'Train as Planned', domain: 'personal', area: 'fitness',
        behaviour: 'Complete a planned session. At most one qualifying session per local day.' },
      { id: 'H02', title: 'Follow My Eating Practice', domain: 'personal', area: 'nourishment',
        behaviour: 'At my selected daily meal, eat a meal that includes a starchy food, a protein food, and a vegetable or fruit. Planning, shopping or photographing it alone does not count.' },
      { id: 'H03', title: 'Follow My Hydration Practice', domain: 'personal', area: 'nourishment',
        behaviour: 'Use the saved cue and water arrangement, and actually drink.' },
      { id: 'H04', title: 'Keep My Sleep Routine', domain: 'personal', area: 'rest',
        behaviour: 'Start the saved preparation sequence within the selected window and perform its steps. Selecting a bedtime does not count as performing it.' },
      { id: 'H05', title: 'Complete My Recovery Practice', domain: 'personal', area: 'rest',
        behaviour: 'Perform the selected recovery practice to its saved completion definition. Counted as completed occurrences, not elapsed days.' },
      { id: 'H06', title: 'Review My Practice', domain: 'personal', area: 'fitness',
        behaviour: 'Look at the recent practice record, choose what to keep or change, and save the next opportunities.' },
    ],
    ability: {
      id: 'ATHLETIC-LIFESTYLE', title: 'Athletic Lifestyle', domain: 'personal',
      description: 'The capability this pathway develops: training, nourishment, rest and review, kept up as a way of living rather than a programme you finish.',
      /* ⚠️ Declared in TIER ORDER — foundation first — which is not the numeric
         order of the ids. S04 and S10 are foundation; S02 and S09 are proficient. */
      skills: [
        { id: 'S01', title: 'Read a Training Plan', tier: 'foundation' },
        { id: 'S04', title: 'Follow a Movement Demonstration', tier: 'foundation' },
        { id: 'S10', title: 'Prepare My Training Setup', tier: 'foundation' },
        { id: 'S07', title: 'Prepare My Eating Practice', tier: 'developing' },
        { id: 'S08', title: 'Organize My Hydration Practice', tier: 'developing' },
        { id: 'S05', title: 'Apply Selected Foundational Movement Skills', tier: 'intermediate' },
        { id: 'S13', title: 'Start with a Reliable Cue', tier: 'intermediate' },
        { id: 'S02', title: 'Match Practice to a Fitness Focus', tier: 'proficient' },
        { id: 'S09', title: 'Organize Sleep and Recovery Support', tier: 'proficient' },
        { id: 'S06', title: 'Use a Routine Alternative', tier: 'advanced' },
        { id: 'S11', title: 'Build a Workable Practice Plan', tier: 'advanced' },
        { id: 'S03', title: 'Interpret Practice Feedback', tier: 'expert' },
        { id: 'S12', title: 'Adapt My Practice Arrangement', tier: 'expert' },
        { id: 'S14', title: 'Return After an Interruption', tier: 'mastery' },
        { id: 'S15', title: 'Review and Continue My Practice', tier: 'mastery' },
      ],
    },
  },

  /* ⚠️⚠️ EVERY TITLE BELOW IS THE PACKAGE AUTHOR'S, COPIED, NOT WRITTEN HERE —
     J01 from the Rank 1 Content Package, J02 from Rank 5's Completion
     Requirements, J03 from Rank 7's. They existed all along; the schema held
     only ids, so the install would have named fifteen tasks `J01-T01`.
     ⚠️ J03's own title was MINE ('Carry My Athletic Lifestyle Forward') where
     the Build Package and Rank 7 both say 'Prepare My Ongoing Athletic
     Lifestyle'. Corrected — a build must not put words in the author's mouth. */
  projects: [
    {
      id: 'J01',
      title: 'Prepare My First Practice Week',
      tasks: [
        { id: 'J01-T01', title: 'Choose my starter route' },
        { id: 'J01-T02', title: 'Place my first three sessions' },
        { id: 'J01-T03', title: 'Ready my space' },
        { id: 'J01-T04', title: 'Ready my eating practice' },
        { id: 'J01-T05', title: 'Save my first week' },
      ],
    },
    {
      id: 'J02',
      title: 'Make My Practice Work in Everyday Life',
      tasks: [
        { id: 'J02-T01', title: 'Identify the constraint' },
        { id: 'J02-T02', title: 'Choose a workable response' },
        { id: 'J02-T03', title: 'Ready the change' },
        { id: 'J02-T04', title: 'Update the same plan' },
        { id: 'J02-T05', title: 'Put it into use' },
      ],
    },
    {
      id: 'J03',
      title: 'Prepare My Ongoing Athletic Lifestyle',
      tasks: [
        { id: 'J03-T01', title: 'Select what I will retain' },
        { id: 'J03-T02', title: 'Arrange ongoing practice' },
        { id: 'J03-T03', title: 'Prepare my return approach' },
        { id: 'J03-T04', title: 'Use and review the arrangement' },
        { id: 'J03-T05', title: 'Save the continuation version' },
      ],
    },
  ],

  ranks: [{
    rank: 1,
    title: 'Begin the Practice',
    blurb: 'A prepared first week, and the first training, eating and hydration practices put into use.',
    quests: [
      {
        id: 'Q01',
        title: 'Put Your Core Practice in Motion',
        blurb: 'I have put my training, eating and hydration practices into use.',
        goals: [
          {
            id: 'Q01-G01',
            title: 'Complete seven planned sessions',
            evidenceLine: 'Workout practice · sessions since this rank began',
            /* "Seven distinct completed sessions within this rank's window; at
               most one qualifying session per local day in this starter version."
               ⚠️ The linked action is "H01 Train as Planned; AP01 session
               occurrences" — one session can be evidenced either way, but it is
               ONE event and counts once. Which is the adapter's job, not this
               predicate's: whatever records a session writes an H01 date. */
            all: [{ kind: 'distinct_dates', habit: 'H01', count: 7, per: 'day' }],
          },
          {
            id: 'Q01-G02',
            title: 'Put my eating practice into use',
            evidenceLine: 'Eating practice · seven distinct qualifying days',
            /* "Seven distinct local dates on which the saved eating behavior was
               performed." */
            all: [{ kind: 'distinct_dates', habit: 'H02', count: 7, per: 'day' }],
          },
          {
            id: 'Q01-G03',
            title: 'Put my hydration practice into use',
            evidenceLine: 'Hydration practice · seven distinct qualifying days',
            /* "Seven distinct local dates on which the saved hydration behavior
               was performed." */
            all: [{ kind: 'distinct_dates', habit: 'H03', count: 7, per: 'day' }],
          },
        ],
      },
      {
        id: 'Q02',
        title: 'Prepare to Keep Going',
        blurb: 'My first practice week is prepared and I can use the starter instructions and setup.',
        goals: [
          {
            id: 'Q02-G01',
            title: 'Prepare my first practice week',
            evidenceLine: 'Prepare My First Practice Week · five tasks',
            /* "All five project tasks below completed." */
            all: [{ kind: 'tasks', project: 'J01' }],
          },
          {
            id: 'Q02-G02',
            title: 'Use the opening guidance',
            evidenceLine: 'Opening lessons · three to read',
            /* "AP01-A00, AP03-N01 and AP03-N02 completed; recognize eligible
               earlier completion."
               ⚠️⚠️ THAT LAST CLAUSE IS WHY `fresh` DEFAULTS TO FALSE. Reading the
               opening lesson during setup — before this rank's window opened —
               still counts. Two programs, so two predicates. */
            all: [
              { kind: 'occurrences', program: 'AP01', ids: ['AP01-A00'], mode: 'all' },
              { kind: 'occurrences', program: 'AP03', ids: ['AP03-N01', 'AP03-N02'], mode: 'all' },
            ],
          },
          {
            id: 'Q02-G03',
            title: 'Complete Foundation skills',
            evidenceLine: 'Athletic Lifestyle · Foundation tier',
            /* "S01, S04 and S10 completed through their own requirements."
               ⚠️ THREE, not four. The prototype still renders 4 / 4; the Final
               Consistency Review corrected the inventory and the prototype was
               never updated. */
            all: [{ kind: 'skills', ids: ['S01', 'S04', 'S10'] }],
          },
        ],
      },
    ],
  },

  /* ── Rank 2 ─────────────────────────────────────────────────────────────
     Source: `Athlete GPB - Rank 2 Completion Requirements v0.1.md`.
     ⚠️ THE ONLY RANK WITH THREE QUESTS. Every other rank has two, and the
     package fixes the range at one to three. */
  {
    rank: 2,
    title: 'Add Support',
    blurb: 'The core rhythm continues while rest, recovery and nourishment are put on a footing.',
    quests: [
      {
        id: 'Q03',
        title: 'Strengthen Your Core Rhythm',
        blurb: 'My training, eating and hydration practices continue, and the starter program is finished.',
        goals: [
          {
            id: 'Q03-G01',
            title: 'Complete four further planned sessions',
            evidenceLine: 'Workout practice · sessions since this rank began',
            /* "Four qualifying completed sessions after the Rank 2 window opens;
               no more than one qualifying session per local day." */
            all: [{ kind: 'distinct_dates', habit: 'H01', count: 4, per: 'day' }],
          },
          {
            id: 'Q03-G02',
            title: 'Continue my eating practice',
            evidenceLine: 'Eating practice · fourteen distinct qualifying days',
            /* "Fourteen distinct qualifying dates in the new rank window." */
            all: [{ kind: 'distinct_dates', habit: 'H02', count: 14, per: 'day' }],
          },
          {
            id: 'Q03-G03',
            title: 'Continue my hydration practice',
            evidenceLine: 'Hydration practice · fourteen distinct qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H03', count: 14, per: 'day' }],
          },
          {
            id: 'Q03-G04',
            title: 'Finish Foundation Prep',
            evidenceLine: 'Athlete Foundation Prep · seventeen core occurrences',
            /* "Its seventeen required core occurrences completed: A00, S01–S12
               and R01–R04." ⚠️ Not fresh: the run began in Rank 1 and its
               evidence stays available. */
            all: [{ kind: 'occurrences', program: 'AP01', ids: AP01_CORE, mode: 'all' }],
          },
        ],
      },
      {
        id: 'Q04',
        title: 'Establish Your Rest Practices',
        blurb: 'A sleep routine and a recovery practice are chosen, prepared and in use.',
        goals: [
          {
            id: 'Q04-G01',
            title: 'Prepare my sleep and recovery arrangement',
            evidenceLine: 'Sleep and recovery · three modules, three arrangements',
            /**
             * ⚠️⚠️ **THE FIRST GOAL THAT IS NOT ONE PREDICATE.** The package:
             * "the opening goal passes when the relevant instruction is read AND
             * those usable arrangements are saved." Reading is occurrences;
             * saving is three records with their own named fields, spelled out
             * in the rank's "exact opening-module contract for AP04".
             */
            all: [
              { kind: 'occurrences', program: 'AP04', ids: ['AP04-O01', 'AP04-O02', 'AP04-O03'], mode: 'all' },
              { kind: 'record', record: 'AP04-O01-arrangement', fields: ['sleepPeriod', 'startWindow', 'preparationSequence'] },
              { kind: 'record', record: 'AP04-O02-practice', fields: ['name', 'steps', 'amount', 'frequency', 'resources', 'cue'] },
              { kind: 'record', record: 'AP04-O03-opportunities', fields: ['nextSleepPreparation', 'nextRecovery'] },
            ],
          },
          {
            id: 'Q04-G02',
            title: 'Use my sleep routine',
            evidenceLine: 'Sleep routine · seven distinct evenings',
            /* "Seven qualifying evenings/sleep-preparation occasions on distinct
               local dates after the rank window opens." ⚠️ A routine that runs
               past midnight uses its START date, consistently — the adapter's
               problem, but the reason this counts days and not sessions. */
            all: [{ kind: 'distinct_dates', habit: 'H04', count: 7, per: 'day' }],
          },
          {
            id: 'Q04-G03',
            title: 'Use my recovery practice',
            evidenceLine: 'Recovery practice · three completed opportunities',
            /**
             * ⚠️⚠️ **OPPORTUNITIES, NOT DAYS** — the only shape in Rank 1–2 that
             * needs the other channel. "Three distinct completed opportunities
             * after the rank window opens, at the selected practice's authored
             * frequency", and the habit's own rule is explicit: "count actual
             * completed occurrences, not elapsed days… do not award extra
             * occurrences by dividing one practice into several logs."
             */
            all: [{ kind: 'distinct_dates', habit: 'H05', count: 3, per: 'opportunity' }],
          },
        ],
      },
      {
        id: 'Q05',
        title: 'Make Nourishment Work for You',
        blurb: 'The eating program reaches its second-block milestone and the Developing tier is complete.',
        goals: [
          {
            id: 'Q05-G01',
            title: "Complete the eating program's second-block milestone",
            evidenceLine: 'Eating for My Athletic Practice · thirty-four occurrences',
            /* "N01–N06 and D01–D14 eating/hydration core occurrences complete,
               using actual dates and permitted rescheduling."
               ⚠️ Each authored day carries an -E AND an -H, so D01–D14 is 28
               occurrences. With N01–N06 that is 34, not 20. */
            all: [{
              kind: 'occurrences', program: 'AP03', mode: 'all',
              ids: [
                'AP03-N01', 'AP03-N02', 'AP03-N03', 'AP03-N04', 'AP03-N05', 'AP03-N06',
                ...ap03Days(14, 'E'), ...ap03Days(14, 'H'),
              ],
            }],
          },
          {
            id: 'Q05-G02',
            title: 'Complete the Developing tier',
            evidenceLine: 'Athletic Lifestyle · Developing tier',
            /* "S07 and S08 complete through the requirements below." */
            all: [{ kind: 'skills', ids: ['S07', 'S08'] }],
          },
        ],
      },
    ],
  },

  /* ── Rank 3 ─────────────────────────────────────────────────────────────
     Source: `Athlete GPB - Rank 3 Completion Requirements v0.1.md`. */
  {
    rank: 3,
    title: 'Build a Rhythm',
    blurb: 'The first development block is followed alongside the supporting practices, and a short review prepares what comes next.',
    quests: [
      {
        id: 'Q06',
        title: 'Establish Your Development Rhythm',
        blurb: 'I have used the opening development sequence alongside my supporting practices and prepared what comes next.',
        goals: [
          {
            id: 'Q06-G01',
            title: 'Complete Athletic Development Block A',
            evidenceLine: 'Athletic Development · Block A, ten occurrences',
            /* "All ten named required core occurrences below complete."
               ⚠️⚠️ NO H01 GOAL EXISTS IN RANKS 3–5. The package: "the actual
               Block A sessions also satisfy H01. There is no additional
               duplicate workout quota in this quest." Training continues — it is
               simply evidenced by the program rather than by a habit threshold,
               which is why H01's fresh-date streak has a ~42-day hole through
               the middle of the pathway. Anything drawing habit continuity will
               show that hole; it is authored, not a bug. */
            all: [{
              kind: 'occurrences', program: 'AP02', mode: 'all',
              ids: ['AP02-A-L01', 'AP02-A-L02', 'AP02-A-L03', 'AP02-A-S01', 'AP02-A-S02',
                'AP02-A-S03', 'AP02-A-S04', 'AP02-A-S05', 'AP02-A-S06', 'AP02-A-R01'],
            }],
          },
          {
            id: 'Q06-G02',
            title: 'Continue my eating practice',
            evidenceLine: 'Eating practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H02', count: 7, per: 'day' }],
          },
          {
            id: 'Q06-G03',
            title: 'Continue my hydration practice',
            evidenceLine: 'Hydration practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H03', count: 7, per: 'day' }],
          },
          {
            id: 'Q06-G04',
            title: 'Continue my sleep routine',
            evidenceLine: 'Sleep routine · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H04', count: 7, per: 'day' }],
          },
          {
            id: 'Q06-G05',
            title: 'Continue my recovery practice',
            evidenceLine: 'Recovery practice · three further opportunities',
            all: [{ kind: 'distinct_dates', habit: 'H05', count: 3, per: 'opportunity' }],
          },
          {
            id: 'Q06-G06',
            title: 'Review and prepare the next opportunities',
            evidenceLine: 'Practice review · one, with its next opportunities saved',
            /**
             * ⚠️⚠️ **THE FIRST `fresh` RECORD, AND THE PACKAGE SAYS WHY IN ONE
             * SENTENCE:** "Valid program and skill completion may predate the
             * rank… but the fresh H06 goal still needs a review in this rank's
             * window." An earlier review can satisfy AP02-A-R01; it cannot
             * satisfy this.
             *
             * ⭐ Two parts for one action, deliberately. The review IS an H06
             * event AND a saved output — "done when the observation/choice and
             * next opportunities are saved" — and somebody can tick the habit
             * without filling the form. 1 of 2 is then the honest reading:
             * you logged the review but did not save what comes next.
             */
            all: [
              { kind: 'distinct_dates', habit: 'H06', count: 1, per: 'day' },
              {
                kind: 'record', fresh: true, record: 'R04-review',
                fields: ['observation', 'choice', 'nextOpportunities'],
              },
            ],
          },
        ],
      },
      {
        id: 'Q07',
        title: 'Make the Foundations Usable',
        blurb: 'The eating program is finished and the Intermediate tier is complete.',
        goals: [
          {
            id: 'Q07-G01',
            title: 'Finish Eating for My Athletic Practice',
            evidenceLine: 'Eating for My Athletic Practice · all sixty-five occurrences',
            /* "N01–N09, D01–D28 eating occurrences and D01–D28 hydration
               occurrences complete: the 65 required core occurrences." */
            all: [{
              kind: 'occurrences', program: 'AP03', mode: 'all',
              ids: [
                ...Array.from({ length: 9 }, (_, i) => `AP03-N0${i + 1}`),
                ...ap03Days(28, 'E'), ...ap03Days(28, 'H'),
              ],
            }],
          },
          {
            id: 'Q07-G02',
            title: 'Complete the Intermediate tier',
            evidenceLine: 'Athletic Lifestyle · Intermediate tier',
            all: [{ kind: 'skills', ids: ['S05', 'S13'] }],
          },
        ],
      },
    ],
  },

  /* ── Rank 4 ─────────────────────────────────────────────────────────────
     Source: `Athlete GPB - Rank 4 Completion Requirements v0.1.md`. */
  {
    rank: 4,
    title: 'Develop Deliberately',
    blurb: 'A training focus is chosen and followed, and the sleep and recovery setup is finished.',
    quests: [
      {
        id: 'Q08',
        title: 'Train with a Clear Focus',
        blurb: 'I have chosen a focus, followed the matching block and can organise support around it.',
        goals: [
          {
            id: 'Q08-G01',
            title: 'Choose my training focus',
            evidenceLine: 'Training focus · one chosen, with its variant and sessions',
            /**
             * "One focus and one matching available program variant saved with
             * the activity-to-focus mapping", and its task: "save one selected
             * focus, the matching authored variant and two of its relevant
             * planned sessions."
             * ⚠️ NOT fresh — the text says neither "fresh" nor "in the window",
             * and a focus chosen while previewing the block is still the focus.
             */
            all: [{
              kind: 'record', record: 'AP02-B-focus',
              fields: ['focus', 'variant', 'plannedSessions'],
            }],
          },
          {
            id: 'Q08-G02',
            title: 'Complete Athletic Development Block B',
            evidenceLine: 'Athletic Development · Block B, nine occurrences',
            all: [{
              kind: 'occurrences', program: 'AP02', mode: 'all',
              ids: ['AP02-B-L01', 'AP02-B-L02', 'AP02-B-S01', 'AP02-B-S02', 'AP02-B-S03',
                'AP02-B-S04', 'AP02-B-S05', 'AP02-B-S06', 'AP02-B-R01'],
            }],
          },
          {
            id: 'Q08-G03',
            title: 'Complete the Proficient tier',
            evidenceLine: 'Athletic Lifestyle · Proficient tier',
            all: [{ kind: 'skills', ids: ['S02', 'S09'] }],
          },
        ],
      },
      {
        id: 'Q09',
        title: 'Support the Work You Are Doing',
        blurb: 'The sleep and recovery programme is finished while the supporting practices continue.',
        goals: [
          {
            id: 'Q09-G01',
            title: 'Finish Sleep and Recovery Setup',
            evidenceLine: 'Sleep and Recovery Setup · twenty-six occurrences',
            /* "Its complete named core set below is complete; eligible earlier
               work recognized." ⚠️ The run opened in Rank 2 — three of these
               occurrences ARE Q04-G01's opening modules, and they count once. */
            all: [{ kind: 'occurrences', program: 'AP04', ids: AP04_CORE, mode: 'all' }],
          },
          {
            id: 'Q09-G02',
            title: 'Continue my eating practice',
            evidenceLine: 'Eating practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H02', count: 7, per: 'day' }],
          },
          {
            id: 'Q09-G03',
            title: 'Continue my hydration practice',
            evidenceLine: 'Hydration practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H03', count: 7, per: 'day' }],
          },
          {
            id: 'Q09-G04',
            title: 'Continue my sleep routine',
            evidenceLine: 'Sleep routine · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H04', count: 7, per: 'day' }],
          },
          {
            id: 'Q09-G05',
            title: 'Continue my recovery practice',
            evidenceLine: 'Recovery practice · three further opportunities',
            all: [{ kind: 'distinct_dates', habit: 'H05', count: 3, per: 'opportunity' }],
          },
          {
            id: 'Q09-G06',
            title: 'Review and prepare what comes next',
            evidenceLine: 'Practice review · one, with its keep/change choice saved',
            /**
             * "One fresh completed review with a saved keep/change choice and
             * next opportunities."
             * ⚠️⚠️ SAME record id as Rank 3's review, and deliberately: the review
             * is one form a person fills in again each cycle, not a new form per
             * rank. `fresh` is what makes the rank ask for a NEW one — the most
             * recent save has to fall inside this window — without inventing a
             * per-rank record id that the app would then have to store seven of.
             */
            all: [
              { kind: 'distinct_dates', habit: 'H06', count: 1, per: 'day' },
              {
                kind: 'record', fresh: true, record: 'R04-review',
                fields: ['observation', 'choice', 'nextOpportunities'],
              },
            ],
          },
        ],
      },
    ],
  },

  /* ── Rank 5 ─────────────────────────────────────────────────────────────
     Source: `Athlete GPB - Rank 5 Completion Requirements v0.1.md`. */
  {
    rank: 5,
    title: 'Make It Fit',
    blurb: 'The development sequence is finished, a practice alternative is used, and the arrangement bends around a real constraint.',
    quests: [
      {
        id: 'Q10',
        title: 'Complete Your Development Sequence',
        blurb: 'I have completed the selected development program and used an appropriate alternative within its practice.',
        goals: [
          {
            id: 'Q10-G01',
            title: 'Finish Athletic Development',
            evidenceLine: 'Athletic Development · all twenty-eight occurrences',
            /* "Required core sets from Blocks A, B and C complete: 28 named
               occurrences." ⚠️ "A generic program-complete label does not
               substitute for the named core work." */
            all: [{ kind: 'occurrences', program: 'AP02', ids: AP02_CORE, mode: 'all' }],
          },
          {
            id: 'Q10-G02',
            title: 'Put an authored session alternative into use',
            evidenceLine: 'One Block C session · performed as its alternative',
            /**
             * "Select and complete one supported alternative within the chosen
             * run's eligible session work."
             * ⚠️⚠️ THIS IS WHY `variant` EXISTS. The alternative is not a separate
             * occurrence — it is one of Block C's ordinary sessions performed in
             * its authored alternative version, and "its actual version must be
             * recorded". Expressed as "any one session" it would be satisfied by
             * any ordinary session, which the package forbids: "rescheduling the
             * unchanged session … does not by itself demonstrate S06's use of an
             * alternative practice sequence."
             */
            all: [{
              kind: 'occurrences', program: 'AP02', mode: 'any', variant: 'alternative',
              ids: ['AP02-C-S01', 'AP02-C-S02', 'AP02-C-S03', 'AP02-C-S04', 'AP02-C-S05', 'AP02-C-S06'],
            }],
          },
        ],
      },
      {
        id: 'Q11',
        title: 'Make the Lifestyle Work in Your Life',
        blurb: 'I have addressed a practical constraint, used the revised arrangement, and continued the habits supporting my training.',
        goals: [
          {
            id: 'Q11-G01',
            title: 'Make my practice work in everyday life',
            evidenceLine: 'Make My Practice Work in Everyday Life · five tasks',
            /**
             * "A concrete constraint is addressed in the saved arrangement and
             * the change has actually been used."
             * ⚠️ That reads like a saved record, and it is not: the linked action
             * is "J02 and its five tasks below", and the "actually used" half is
             * T05's own done-when — "at least one matching actual action
             * occurrence is recorded". One project, five tasks, and the package
             * is emphatic that writing the plan is not finishing it: "merely
             * imagining a problem or writing an unused fallback does not
             * complete T05."
             */
            all: [{ kind: 'tasks', project: 'J02' }],
          },
          {
            id: 'Q11-G02',
            title: 'Continue my eating practice',
            evidenceLine: 'Eating practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H02', count: 7, per: 'day' }],
          },
          {
            id: 'Q11-G03',
            title: 'Continue my hydration practice',
            evidenceLine: 'Hydration practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H03', count: 7, per: 'day' }],
          },
          {
            id: 'Q11-G04',
            title: 'Continue my sleep routine',
            evidenceLine: 'Sleep routine · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H04', count: 7, per: 'day' }],
          },
          {
            id: 'Q11-G05',
            title: 'Continue my recovery practice',
            evidenceLine: 'Recovery practice · three further opportunities',
            all: [{ kind: 'distinct_dates', habit: 'H05', count: 3, per: 'opportunity' }],
          },
          {
            id: 'Q11-G06',
            title: 'Review and prepare the next opportunities',
            evidenceLine: 'Practice review · one, with its practical choice saved',
            all: [
              { kind: 'distinct_dates', habit: 'H06', count: 1, per: 'day' },
              {
                kind: 'record', fresh: true, record: 'R04-review',
                fields: ['observation', 'choice', 'nextOpportunities'],
              },
            ],
          },
          {
            id: 'Q11-G07',
            title: 'Complete the Advanced tier',
            evidenceLine: 'Athletic Lifestyle · Advanced tier',
            all: [{ kind: 'skills', ids: ['S06', 'S11'] }],
          },
        ],
      },
    ],
  },

  /* ── Rank 6 ─────────────────────────────────────────────────────────────
     Source: `Athlete GPB - Rank 6 Completion Requirements v0.1.md`. */
  {
    rank: 6,
    title: 'Refine the Approach',
    blurb: 'One bounded adjustment is tried against the practice record, and the lifestyle keeps running around it.',
    quests: [
      {
        id: 'Q12',
        title: 'Use Feedback to Improve Your Practice',
        blurb: 'I have read my own practice record, tried one adjustment and decided what to do with it.',
        goals: [
          {
            id: 'Q12-G01',
            title: 'Complete the first maintenance block',
            evidenceLine: 'Maintain My Athletic Lifestyle · Block A, in order',
            /**
             * ⚠️⚠️ **THE ONLY ORDERED SET IN THE BUILD**, and the order is the
             * lesson rather than bookkeeping: read the guidance, observe the
             * record, choose the adjustment, apply it twice, then judge it.
             * The package times each step against the last — "after T01, before
             * applying the change", "after both applications" — so an adjustment
             * chosen after the applications it supposedly governs is not a trial,
             * it is a story told afterwards.
             * ⭐ Any of the three outcomes finishes it: "a trial that did not
             * succeed can still supply learning evidence."
             */
            all: [{
              kind: 'occurrences', program: 'AP05', mode: 'all', ordered: true,
              ids: ['AP05-A-L01', 'AP05-A-T01', 'AP05-A-T02', 'AP05-A-P01', 'AP05-A-P02', 'AP05-A-R01'],
            }],
          },
          {
            id: 'Q12-G02',
            title: 'Complete the Expert tier',
            evidenceLine: 'Athletic Lifestyle · Expert tier',
            all: [{ kind: 'skills', ids: ['S03', 'S12'] }],
          },
        ],
      },
      {
        id: 'Q13',
        title: 'Keep the Lifestyle Working',
        blurb: 'The training and supporting practices continue while the adjustment is tried.',
        goals: [
          {
            id: 'Q13-G01',
            title: 'Continue planned training',
            evidenceLine: 'Workout practice · four further sessions',
            /**
             * ⚠️⚠️ H01 RETURNS AFTER THREE RANKS AWAY. Ranks 3–5 carried no
             * training threshold because AP02's blocks supplied the evidence;
             * AP02 is finished, so the habit carries it again.
             * ⚠️ And the package guards the obvious mistake: "the AP05
             * applications can be among these same events… do not add four
             * training sessions on top of four that were already performed in
             * the window." One event, several references, counted once.
             */
            all: [{ kind: 'distinct_dates', habit: 'H01', count: 4, per: 'day' }],
          },
          {
            id: 'Q13-G02',
            title: 'Continue my eating practice',
            evidenceLine: 'Eating practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H02', count: 7, per: 'day' }],
          },
          {
            id: 'Q13-G03',
            title: 'Continue my hydration practice',
            evidenceLine: 'Hydration practice · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H03', count: 7, per: 'day' }],
          },
          {
            id: 'Q13-G04',
            title: 'Continue my sleep routine',
            evidenceLine: 'Sleep routine · seven further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H04', count: 7, per: 'day' }],
          },
          {
            id: 'Q13-G05',
            title: 'Continue my recovery practice',
            evidenceLine: 'Recovery practice · three further opportunities',
            all: [{ kind: 'distinct_dates', habit: 'H05', count: 3, per: 'opportunity' }],
          },
          {
            id: 'Q13-G06',
            title: 'Review and prepare the next opportunities',
            evidenceLine: 'Practice review · one, with its saved choice',
            all: [
              { kind: 'distinct_dates', habit: 'H06', count: 1, per: 'day' },
              {
                kind: 'record', fresh: true, record: 'R04-review',
                fields: ['observation', 'choice', 'nextOpportunities'],
              },
            ],
          },
        ],
      },
    ],
  },

  /* ── Rank 7 ─────────────────────────────────────────────────────────────
     Source: `Athlete GPB - Rank 7 Completion Requirements v0.1.md`.
     ⚠️ The pathway ENDS here. No Rank 8, no recertification: "completing Rank 7
     ends this pathway", and the practices simply continue. */
  {
    rank: 7,
    title: 'Carry It Forward',
    blurb: 'The continuation arrangement is in use, the return approach is practised, and the lifestyle carries on beyond the pathway.',
    quests: [
      {
        id: 'Q14',
        title: 'Make the Practice Your Own',
        blurb: 'I have prepared and used a continuation arrangement, applied a return approach, and finished the maintenance programme.',
        goals: [
          {
            id: 'Q14-G01',
            title: 'Prepare my ongoing athletic lifestyle',
            evidenceLine: 'Carry My Athletic Lifestyle Forward · five tasks',
            /* "The retained arrangement, support, return approach and review
               rhythm are saved, used and finalized." ⚠️ Same shape as Q11-G01:
               the prose describes a record and the LINKED ACTION is "J03 and its
               five tasks below". The link is the specification. */
            all: [{ kind: 'tasks', project: 'J03' }],
          },
          {
            id: 'Q14-G02',
            title: 'Finish Maintain My Athletic Lifestyle',
            evidenceLine: 'Maintain My Athletic Lifestyle · fourteen occurrences',
            /* "All six Block A and eight Block B core occurrences complete."
               ⚠️ Block A is Rank 6's ordered set and is NOT re-ordered here —
               finishing the programme asks for the work, not the sequence twice. */
            all: [{
              kind: 'occurrences', program: 'AP05', mode: 'all',
              ids: ['AP05-A-L01', 'AP05-A-T01', 'AP05-A-T02', 'AP05-A-P01', 'AP05-A-P02', 'AP05-A-R01',
                'AP05-B-L01', 'AP05-B-T01', 'AP05-B-T02', 'AP05-B-P01', 'AP05-B-R01',
                'AP05-B-P02', 'AP05-B-R02', 'AP05-B-T03'],
            }],
          },
          {
            id: 'Q14-G03',
            title: 'Complete the Mastery tier',
            evidenceLine: 'Athletic Lifestyle · Mastery tier',
            all: [{ kind: 'skills', ids: ['S14', 'S15'] }],
          },
        ],
      },
      {
        id: 'Q15',
        title: 'Carry Your Athletic Lifestyle Forward',
        blurb: 'The practices run under the continuation arrangement, and two reviews consider successive application.',
        goals: [
          {
            id: 'Q15-G01',
            title: 'Continue planned training',
            evidenceLine: 'Workout practice · four further sessions',
            all: [{ kind: 'distinct_dates', habit: 'H01', count: 4, per: 'day' }],
          },
          {
            id: 'Q15-G02',
            title: 'Continue my eating practice',
            evidenceLine: 'Eating practice · fourteen further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H02', count: 14, per: 'day' }],
          },
          {
            id: 'Q15-G03',
            title: 'Continue my hydration practice',
            evidenceLine: 'Hydration practice · fourteen further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H03', count: 14, per: 'day' }],
          },
          {
            id: 'Q15-G04',
            title: 'Continue my sleep routine',
            evidenceLine: 'Sleep routine · fourteen further qualifying days',
            all: [{ kind: 'distinct_dates', habit: 'H04', count: 14, per: 'day' }],
          },
          {
            id: 'Q15-G05',
            title: 'Continue my recovery practice',
            evidenceLine: 'Recovery practice · four further opportunities',
            all: [{ kind: 'distinct_dates', habit: 'H05', count: 4, per: 'opportunity' }],
          },
          {
            id: 'Q15-G06',
            title: 'Review and arrange what comes next',
            evidenceLine: 'Practice review · two, with practice between them',
            /**
             * ⚠️⚠️ **THE ONLY CHAIN IN THE BUILD.** "Two qualifying reviews on
             * distinct dates, with an actual subsequent practice occurrence
             * between them" — and the package says why: "the reviews happen on
             * distinct dates, with P02 between them, so they consider successive
             * application rather than duplicate the same review."
             *
             * Three parts express exactly that: two reviews (distinct by
             * counting), a practice after the first, and a review after that
             * practice. The count of two is what closes the same-day loophole —
             * one review cannot be both ends of the chain.
             */
            all: [
              { kind: 'distinct_dates', ref: 'reviews', habit: 'H06', count: 2, per: 'day' },
              {
                kind: 'occurrences', ref: 'between', after: 'reviews',
                program: 'AP05', ids: ['AP05-B-P02'], mode: 'any',
              },
              { kind: 'distinct_dates', after: 'between', habit: 'H06', count: 1, per: 'day' },
            ],
          },
        ],
      },
    ],
  }],
};
